import { AbstractSegmentsService } from '@abstract-services/segments/1.0/segments.abstract-service';
import * as ngCommonHttp from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CustomerUser,
  PagedResourcesSegment,
  Segment,
  SegmentResponse,
  SegmentUsers,
  WalletCoupon,
} from '@generated/tcea/models';
import { SegmentationService } from '@generated/tcea/services/segmentation.service';
import { Observable, throwError } from 'rxjs';
import { catchError, expand, map } from 'rxjs/operators';
import { CustomerUsersApi, WalletApi } from '@constants/api';

@Injectable({
  providedIn: 'root',
})
export class SegmentsService extends AbstractSegmentsService {
  customerUsersApi: string;
  walletUsersApi: string;
  walletCoupons: string;
  walletUserCouponsApi: string;

  constructor(
    private http: ngCommonHttp.HttpClient,
    private generatedSegmentsService: SegmentationService,
  ) {
    super();
    this.customerUsersApi = CustomerUsersApi.GET;
    this.walletUsersApi = WalletApi.GET_USERS;
    this.walletUserCouponsApi = WalletApi.WALLET_USER_COUPONS;
    this.walletCoupons = WalletApi.GET_COUPONS;
  }

  /**
   * Gets segments for the current brand context
   *
   * @param brandId [string]
   * @param params
   * @returns observable of a PaginatedResponse for Segments
   */
  get(
    brandId: string,
    params: {
      page?: number;
      query?: {
        availabilityStart?: string;
        availabilityEnd?: string;
        search?: string;
        enabled?: boolean;
        type?: string;
      };
      sort?: {
        direction?: string;
        field?: string;
      };
    },
  ): Observable<PagedResourcesSegment> {
    const { query, page, sort } = params || {};
    const { search, availabilityEnd, availabilityStart, enabled, type } = query || {};
    const filtersArr = [];

    if (availabilityStart) filtersArr.push(`availabilityStart:${availabilityStart} TO 9999-01-01`);
    if (availabilityEnd) filtersArr.push(`availabilityEnd:1111-01-01 TO ${availabilityEnd}`);
    if (enabled !== undefined) filtersArr.push(`enabled:${enabled}`);
    if (type !== undefined) filtersArr.push(`type:${type}`);

    let sortFields;

    if (sort) {
      sortFields = {
        sortField: sort.field,
        sort: sort.direction.toUpperCase(),
      };
    }

    return this.generatedSegmentsService
      .getSegments({
        ActiveTenantIdentifier: brandId,
        page,
        query: search,
        size: 100,
        ...sortFields,
      })
      .pipe(
        map((response: PagedResourcesSegment) => response),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all the segments for the current brand context
   *
   * @param brandId [string]
   * @returns observable of all Segments
   */
  getAllSegments(brandId: string): Observable<Segment[]> {
    return this.get(brandId, {
      page: 0,
    }).pipe(
      expand((response: PagedResourcesSegment) => {
        if (response.pageMetadata.currentPage + 1 < response.pageMetadata.totalPages) {
          return this.get(brandId, { page: response.pageMetadata.currentPage + 1 }).pipe(
            map((response: PagedResourcesSegment) => {
              return response.content;
            }),
          );
        } else {
          return [];
        }
      }),
      map((response: PagedResourcesSegment) => response.content),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets segment for the segmentId provided
   *
   * @param segmentId [string]
   * @param brandId [string]
   * @returns observable of all stores
   */
  getSegmentById(segmentId: string, brandId: string): Observable<Segment> {
    return this.generatedSegmentsService
      .getSegment({ 'Active-Tenant-Identifier': brandId, segmentId })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Creates segment for current brand context
   *
   * @param brandId [string]
   * @param createData [object]
   * @returns observable of all stores
   */
  create(brandId: string, createData): Observable<Segment> {
    return this.generatedSegmentsService
      .createSegment({ 'Active-Tenant-Identifier': brandId, body: createData })
      .pipe(
        map((response: SegmentResponse) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Updates segment for segmentId and brand context
   *
   * @param brandId [string]
   * @param segmentId [string]
   * @param updateData [string]
   * @returns observable of all stores
   */
  update(brandId: string, segmentId, updateData: Segment): Observable<Segment> {
    let body = updateData;
    return this.generatedSegmentsService
      .updateSegment({ 'Active-Tenant-Identifier': brandId, body, segmentId })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  getCustomerUsers(brandId: string, params?: string): Observable<CustomerUser[]> {
    let URL = `${this.customerUsersApi}`;
    if (params) {
      URL = URL.concat(params);
    }
    return this.http.get(URL).pipe(
      map((response: any) => {
        if (response && response?._embedded?.customerUser) {
          const RESULT = response._embedded.customerUser;
          return RESULT || [];
        }
        return [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getWalletUsers(params?: string): Observable<CustomerUser[]> {
    const URL = `${this.walletUsersApi}${params}`;
    return this.http.get(URL).pipe(
      map((response: any) => {
        if (response && response?._embedded?.customerUser) {
          const RESULT = response._embedded.customerUser;
          return RESULT || [];
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getWalletCoupons(): Observable<WalletCoupon[]> {
    const URL = this.walletCoupons;

    return this.http.get(URL).pipe(
      map((response: any) => {
        if (response && response?._embedded?.coupon) {
          const RESULT = response._embedded.coupon;
          return RESULT || [];
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getCouponsForWalletUser(userId: string): Observable<WalletCoupon[]> {
    const URL = this.walletUserCouponsApi.replace('{userId}', userId);

    return this.http.get(URL).pipe(
      map((response: any) => {
        if (response && response?._embedded?.coupon) {
          const RESULT = response._embedded.coupon;
          return RESULT || [];
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  addCouponForWalletUser(userId: string, body: any): Observable<WalletCoupon> {
    const URL = this.walletUserCouponsApi.replace('{userId}', userId);

    return this.http.post(URL, body).pipe(
      map((response: WalletCoupon) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getUsersForSegment(brandId: string, segmentId: string): Observable<SegmentUsers> {
    return this.generatedSegmentsService
      .getUsersInSegment({
        segmentId: segmentId,
        'Active-Tenant-Identifier': brandId,
      })
      .pipe(
        map((response: SegmentUsers) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  addSegmentUser(brandId: string, segmentId: string, userId: string): Observable<any> {
    return this.generatedSegmentsService
      .addUserToSegment({
        userId: userId,
        segmentId: segmentId,
        'Active-Tenant-Identifier': brandId,
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  removeSegmentUser(brandId: string, segmentId: string, userId: string): Observable<CustomerUser> {
    return this.generatedSegmentsService
      .removeUserFromSegment({
        userId: userId,
        segmentId: segmentId,
        'Active-Tenant-Identifier': brandId,
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }
}
